import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { Loader2, Heart, Video, Phone, PhoneMissed } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import DemoProfileBadge from '@/components/DemoProfileBadge';
import VerifiedBadge from '@/components/VerifiedBadge';
import ConnectionProgressBar from '@/components/lifecycle/ConnectionProgressBar';

function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${window.location.origin}/api/files/${p}`;
}

// Compact relative-time formatter for the call preview line.
// Falls back to a date once older than a week.
function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Decide whether the call summary is more "current" than the last chat
 * message. If yes, we render the call line instead of the message preview.
 */
function callIsMoreRecent(lastCall, lastMessage) {
  if (!lastCall) return false;
  if (!lastMessage) return true;
  return new Date(lastCall.at) > new Date(lastMessage.created_at);
}

/**
 * Compact preview row for a call. Icon + text like:
 *   "Missed video call"       (unseen missed → bold red)
 *   "You called · 5m"         (I initiated + hung up)
 *   "Voice call · 1h"         (peer initiated + we both answered)
 */
function CallPreview({ call }) {
  const Icon = call.event === 'missed'
    ? PhoneMissed
    : call.mode === 'voice' ? Phone : Video;
  const missedForMe = call.event === 'missed' && call.direction === 'incoming';
  const missedByMe = call.event === 'missed' && call.direction === 'outgoing';
  let label;
  if (missedForMe) label = `Missed ${call.mode === 'voice' ? 'voice' : 'video'} call`;
  else if (missedByMe) label = `No answer · ${timeAgo(call.at)}`;
  else if (call.direction === 'outgoing') label = `You called · ${timeAgo(call.at)}`;
  else label = `${call.mode === 'voice' ? 'Voice' : 'Video'} call · ${timeAgo(call.at)}`;
  const color = call.unseen_missed ? 'text-[#EF4444] font-semibold' : 'text-zinc-500';
  return (
    <p className={`text-xs truncate flex items-center gap-1.5 ${color}`} data-testid="match-call-preview">
      <Icon size={11} strokeWidth={2.2} className="flex-shrink-0" />
      {label}
    </p>
  );
}

const PAGE_SIZE = 30;

export default function Matches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [shareCta, setShareCta] = useState(null); // { eligible: bool, dismissed: bool }
  const offsetRef = useRef(0);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(async (reset = false) => {
    const offset = reset ? 0 : offsetRef.current;
    try {
      const r = await api.get(`/matches?offset=${offset}&limit=${PAGE_SIZE}`);
      const pageItems = r.data.matches || [];
      setMatches(prev => reset ? pageItems : [...prev, ...pageItems]);
      offsetRef.current = offset + pageItems.length;
      setHasMore(Boolean(r.data.has_more));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadPage(true);
      setLoading(false);
    })();
  }, [loadPage]);

  // Testimonial CTA — appears only for users with ≥30-day matches who have
  // not already submitted a story. Client-side dismissal via localStorage
  // keeps it from re-showing on every visit once seen.
  useEffect(() => {
    const dismissed = localStorage.getItem('share_story_dismissed') === '1';
    if (dismissed) { setShareCta({ eligible: false, dismissed: true }); return; }
    api.get('/testimonials/eligible')
      .then((r) => setShareCta({ eligible: !!r.data?.eligible, dismissed: false }))
      .catch(() => setShareCta({ eligible: false, dismissed: false }));
  }, []);

  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && !loadingMore && hasMore) {
        setLoadingMore(true);
        await loadPage(false);
        setLoadingMore(false);
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, loadPage]);

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#EF4444]" /></div>;

  const newMatches = matches.filter(m => !m.last_message);
  const conversations = matches.filter(m => m.last_message);

  return (
    <div className="min-h-screen bg-white pb-[80px]" data-testid="matches-page">
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <BackNavButton />
        <h1 className="font-brand text-2xl font-[600] tracking-tight">Matches</h1>
      </div>

      {matches.length === 0 ? (
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-black/[0.02] flex items-center justify-center mx-auto mb-4"><Heart className="w-7 h-7 text-zinc-700" /></div>
            <h2 className="text-lg font-bold mb-1">No matches yet</h2>
            <p className="text-zinc-500 text-sm">Keep swiping to find your person.</p>
          </div>
        </div>
      ) : (
        <>
          {shareCta?.eligible && !shareCta?.dismissed && (
            <div className="px-5 mb-4" data-testid="share-story-cta">
              <div className="rounded-2xl p-4 bg-gradient-to-br from-rose-50 to-white border border-rose-100 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Heart className="w-5 h-5 text-[#B91C1C]" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#0F0F10] mb-0.5">Share your Meet to Keep story</p>
                  <p className="text-xs text-zinc-600 leading-relaxed">Your match crossed the 30-day mark. Want to help future users see what&rsquo;s possible?</p>
                  <div className="flex gap-2 mt-2.5">
                    <button
                      onClick={() => navigate('/share-story')}
                      data-testid="share-story-cta-open"
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#0F0F10] text-white hover:bg-black">
                      Share your story
                    </button>
                    <button
                      onClick={() => { localStorage.setItem('share_story_dismissed', '1'); setShareCta((s) => ({ ...s, dismissed: true })); }}
                      data-testid="share-story-cta-dismiss"
                      className="text-xs font-semibold px-3 py-1.5 rounded-full text-zinc-500 hover:text-zinc-800">
                      Not now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* New Matches - Horizontal scroll */}
          {newMatches.length > 0 && (
            <div className="px-5 mb-6">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">New Matches</p>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {newMatches.map(m => (
                  <button key={m.match_id} onClick={() => navigate(`/chat/${m.match_id}`)} data-testid={`new-match-${m.match_id}`}
                    className="flex-shrink-0 flex flex-col items-center gap-2 group">
                    <div className="relative">
                      <div className="w-[68px] h-[68px] rounded-full p-[2px] brand-gradient">
                        <div className="w-full h-full rounded-full overflow-hidden bg-[#F8F9FA]">
                          <img src={getPhotoUrl(m.other_user.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${m.other_user.user_id}`}
                            alt="" className="w-full h-full object-cover" onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${m.other_user.user_id}`; }} />
                        </div>
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold ${(m.v2_dealbreaker_flags || []).length ? 'bg-amber-500 text-white' : 'brand-gradient text-white'}`} data-testid={`new-match-compat-${m.match_id}`}>{m.v2_compatibility_score ?? m.compatibility_score}%</div>
                    </div>
                    <span className="text-xs text-zinc-600 max-w-[70px] truncate">{m.other_user.display_name?.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversations */}
          <div className="px-5">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Messages</p>
            <div className="space-y-1">
              {(conversations.length > 0 ? conversations : matches).map(m => {
                const showCall = callIsMoreRecent(m.last_call, m.last_message);
                const hasUnseenMissed = !!m.last_call?.unseen_missed;
                return (
                <button key={m.match_id} onClick={() => navigate(`/chat/${m.match_id}`)}
                  data-testid={`match-card-${m.match_id}`}
                  aria-label={hasUnseenMissed ? `${m.other_user.display_name}, missed call` : m.other_user.display_name}
                  className="w-full flex items-center gap-3.5 py-3 px-3 rounded-2xl hover:bg-black/[0.02] transition-all text-left">
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-[#F8F9FA] ring-1 ring-white/[0.06]">
                      <img src={getPhotoUrl(m.other_user.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${m.other_user.user_id}`}
                        alt="" className="w-full h-full object-cover" onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${m.other_user.user_id}`; }} />
                    </div>
                    {hasUnseenMissed && (
                      <span
                        data-testid={`missed-call-badge-${m.match_id}`}
                        className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#EF4444] ring-2 ring-white flex items-center justify-center"
                        aria-label="Missed call">
                        <PhoneMissed size={10} strokeWidth={2.6} className="text-white" />
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="font-semibold text-sm truncate">{m.other_user.display_name}{m.other_user.age ? `, ${m.other_user.age}` : ''}</p>
                        <VerifiedBadge badges={m.other_user.badges} size={13} data-testid="matches-verified-checkmark" />
                        {m.other_user.is_seed && <DemoProfileBadge isSeed={true} size="xs" />}
                      </div>
                      {(m.last_call || m.last_message) && <span className="text-[10px] text-zinc-500 flex-shrink-0 ml-2">
                        {timeAgo((showCall ? m.last_call.at : m.last_message?.created_at) || '')}
                      </span>}
                    </div>
                    {showCall ? (
                      <CallPreview call={m.last_call} />
                    ) : m.last_message ? (
                      <p className="text-xs text-zinc-500 truncate">
                        {m.last_message.sender_id === user?.user_id ? 'You: ' : ''}{m.last_message.text}
                      </p>
                    ) : (
                      <p className="text-xs text-[#EF4444] flex items-center gap-1"><Sparkles size={10} className="text-[#EF4444]" /> New match! Say hi</p>
                    )}
                    {/* Feb 27 2026 · Phase 159 — shared-interest hint on the match card.
                        Server returns `shared_interests` (0–5 items, case-insensitive
                        intersect). Skip render when empty so we don't add empty space. */}
                    {Array.isArray(m.shared_interests) && m.shared_interests.length > 0 && (
                      <p className="text-[10.5px] text-zinc-500 truncate mt-0.5"
                         data-testid={`match-shared-interests-${m.match_id}`}
                         title={`You both like ${m.shared_interests.join(', ')}`}>
                        <Sparkles size={9} className="inline text-[#EF4444] mr-1 -mt-px" />
                        You both like {m.shared_interests.slice(0, 3).join(', ')}
                        {m.shared_interests.length > 3 ? ` +${m.shared_interests.length - 3}` : ''}
                      </p>
                    )}
                    {/* Match → Meet → Keep journey bar — visible in-line so users can see
                        the full arc from the Matches list without opening the chat. */}
                    {m.lifecycle && m.lifecycle.lifecycle_stage !== 'archived' && (
                      <div className="mt-2 pr-1">
                        <ConnectionProgressBar lifecycle={m.lifecycle} compact />
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <span
                      data-testid={`match-compat-${m.match_id}`}
                      title={(m.v2_dealbreaker_flags || []).length ? `Dealbreaker: ${m.v2_dealbreaker_flags.join(', ')}` : `Compatibility score v${m.v2_score_version || '1'}`}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(m.v2_dealbreaker_flags || []).length ? 'text-amber-700 bg-amber-100' : 'text-[#EF4444] bg-[#EF4444]/10'}`}>
                      {m.v2_compatibility_score ?? m.compatibility_score}%
                    </span>
                  </div>
                </button>
                );
              })}
            </div>
          </div>
          {/* Infinite scroll sentinel */}
          {hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-6" data-testid="matches-load-more-sentinel">
              {loadingMore && <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Sparkles(props) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>; }
